import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Info, X, AlertTriangle } from 'lucide-react';

const NotificationContext = createContext(null);

const TOAST_STYLES = {
  success: {
    title: 'Success',
    icon: CheckCircle2,
    accent: '#16a34a',
    background: 'linear-gradient(135deg, rgba(240,253,244,0.98), rgba(220,252,231,0.98))',
    border: 'rgba(22,163,74,0.18)',
  },
  error: {
    title: 'Something went wrong',
    icon: AlertCircle,
    accent: '#dc2626',
    background: 'linear-gradient(135deg, rgba(254,242,242,0.98), rgba(254,226,226,0.98))',
    border: 'rgba(220,38,38,0.18)',
  },
  warning: {
    title: 'Attention needed',
    icon: AlertTriangle,
    accent: '#d97706',
    background: 'linear-gradient(135deg, rgba(255,251,235,0.98), rgba(254,243,199,0.98))',
    border: 'rgba(217,119,6,0.18)',
  },
  info: {
    title: 'Heads up',
    icon: Info,
    accent: '#2563eb',
    background: 'linear-gradient(135deg, rgba(239,246,255,0.98), rgba(219,234,254,0.98))',
    border: 'rgba(37,99,235,0.18)',
  },
};

export function NotificationProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(({ type = 'info', title, message, duration = 4500 }) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const toast = {
      id,
      type,
      title: title || TOAST_STYLES[type]?.title || 'Notification',
      message,
      duration,
    };

    setToasts((prev) => [...prev, toast]);
    window.setTimeout(() => removeToast(id), duration);
  }, [removeToast]);

  const value = useMemo(() => ({
    showToast,
    showSuccess: (message, title = 'Success') => showToast({ type: 'success', title, message }),
    showError: (message, title = 'Something went wrong') => showToast({ type: 'error', title, message }),
    showWarning: (message, title = 'Attention needed') => showToast({ type: 'warning', title, message }),
    showInfo: (message, title = 'Heads up') => showToast({ type: 'info', title, message }),
  }), [showToast]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          width: 'min(380px, calc(100vw - 32px))',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => {
          const style = TOAST_STYLES[toast.type] || TOAST_STYLES.info;
          const Icon = style.icon;
          return (
            <div
              key={toast.id}
              style={{
                pointerEvents: 'auto',
                background: style.background,
                border: `1px solid ${style.border}`,
                borderLeft: `5px solid ${style.accent}`,
                borderRadius: 18,
                boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
                backdropFilter: 'blur(18px)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '16px 16px 14px',
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    display: 'grid',
                    placeItems: 'center',
                    background: `${style.accent}18`,
                    color: style.accent,
                    flexShrink: 0,
                  }}
                >
                  <Icon size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{toast.title}</div>
                  <div style={{ color: '#334155', lineHeight: 1.5, fontSize: '0.95rem' }}>{toast.message}</div>
                </div>
                <button
                  type="button"
                  onClick={() => removeToast(toast.id)}
                  aria-label="Dismiss notification"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#64748b',
                    cursor: 'pointer',
                    padding: 2,
                    marginTop: 2,
                  }}
                >
                  <X size={18} />
                </button>
              </div>
              <div
                style={{
                  height: 3,
                  width: '100%',
                  background: `${style.accent}22`,
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                    background: style.accent,
                    transformOrigin: 'left center',
                    animation: `toast-shrink ${toast.duration}ms linear forwards`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes toast-shrink {
          from { transform: scaleX(1); }
          to { transform: scaleX(0); }
        }
      `}</style>
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
}
